import { FieldsRenderer } from '@neo/blocks/forms/fields-renderer'
import type { GenericFormType } from '@neo/blocks/forms/types'
import { Button } from '@neo/index'
import { useMemo } from 'react'
import { useActionAutofill } from 'v2/pages/flow/hooks/use-action-autofill'
import { extractFieldsAndSchemaFromAction } from 'v2/pages/flow/hooks/use-action-fields'
import type { McpToolsResponse } from 'v2/pages/settings/types'
import { SuspenseLoader } from 'v2/shared/components/suspense-loader'
import type { RExternalOrgApp } from 'v2/types/api/settings'
import type { AiActionNodeData } from '../../../types'
import { generateActionFieldConfigs } from './action-field-configs'

// Header component with hooks
const ActionInputsHeader = ({
  form,
  arrayIndex,
  optionalFields,
  nodeVars,
}: {
  form: GenericFormType<AiActionNodeData['config']>
  arrayIndex: number
  optionalFields: string[]
  nodeVars: string[]
}) => {
  const { handleRefreshNodeVars, isMappingParameters } = useActionAutofill(
    form,
    arrayIndex,
    optionalFields,
    nodeVars,
  )

  return (
    <div className="relative pl-8 pt-0 -mt-4">
      {/* Header with refresh button */}
      <div className="flex items-center flex-col -mb-2">
        <span className="text-xs-400 text-secondary mt-4 leading-4">
          Provide values for the fields below. You can either enter them manually or use the button
          to automatically fetch values from node variables.
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRefreshNodeVars}
            disabled={isMappingParameters}
            className="underline p-0 hover:bg-transparent form-button-ghost"
            data-testid="button-refresh-nodevars"
          >
            {isMappingParameters ? (
              <SuspenseLoader text="Populating" variant="inline" />
            ) : (
              'Autofill with node variables'
            )}
          </Button>
        </span>
      </div>
    </div>
  )
}

// Component for dynamic action inputs that reactively updates
export const DynamicActionInputs = ({
  form,
  nodeVars,
  activeApps,
  mcpTools,
  arrayIndex = 0,
}: {
  form: GenericFormType<AiActionNodeData['config']>
  nodeVars: string[]
  activeApps: RExternalOrgApp[] | undefined
  mcpTools: McpToolsResponse | undefined
  arrayIndex?: number
}) => {
  // Get current action name and org_app_uuid reactively
  const currentActionName = form.useStore(
    (state) => state.values.actions?.[arrayIndex]?.action_name,
  )
  const currentOrgAppUuid = form.useStore(
    (state) => state.values.actions?.[arrayIndex]?.org_app_uuid,
  )

  // Calculate dynamic fields based on current action name and org_app_uuid
  const dynamicFields = useMemo(
    () =>
      extractFieldsAndSchemaFromAction(currentActionName, activeApps, mcpTools, currentOrgAppUuid),
    [currentActionName, activeApps, mcpTools, currentOrgAppUuid],
  )
  // Generate field configs for FieldsRenderer
  const fieldConfigs = useMemo(() => {
    if (dynamicFields.required.length === 0 && dynamicFields.optional.length === 0) {
      return []
    }

    const headerComponent = (
      <ActionInputsHeader
        form={form}
        arrayIndex={arrayIndex}
        optionalFields={dynamicFields.optional}
        nodeVars={nodeVars}
      />
    )

    return generateActionFieldConfigs(dynamicFields, nodeVars, arrayIndex, headerComponent)
  }, [dynamicFields.required, dynamicFields.optional, form, arrayIndex, nodeVars])

  if (fieldConfigs.length === 0) {
    return null
  }

  return (
    <div className="relative">
      {/* Main vertical line that spans the entire content */}
      <div
        className="absolute left-2 top-0 bottom-[1rem] border-l-2 border-dotted border-st1"
        aria-hidden="true"
      />
      <FieldsRenderer fields={fieldConfigs} form={form} />
    </div>
  )
}
